import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { fetchOrderById, updateOrderStatusApi } from "./orderService";
import { Order } from "src/shared/services/interfacev2";

export const useOrderDetails = (orderId?: string) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [outstandingAmount, setOutstandingAmount] = useState(0);

  /** Fetch Order Details */
  const fetchOrderDetails = useCallback(async () => {
    if (!orderId) return;

    try {
      setIsLoading(true);
      const orderData = await fetchOrderById(orderId);
      // Set order state with fetched data
      setOrder(orderData);
      const outstanding = order?.totalOutstandingBalance || 0;
      setOutstandingAmount(outstanding);

      // Fetch outstanding balance if customer exists
      // const customerId = orderData?.orders?.[0]?.customerId || (orderData as any)?.Customerid || (orderData as any)?.customerDetails?._id?.$oid;
      // if (customerId) {
      //   const balance = await fetchCustomerLedger(customerId);
      //   setOutstandingAmount(balance);
      // }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  /** Update Order Status */
  const updateOrderStatus = useCallback(
  async (status: string, adjustType: string = "refund") => {
    if (!orderId) return false;

    try {
      // Get latest payment method safely
      const paymentMethod =
        order?.paymentHistory?.[
          (order.paymentHistory?.length || 1) - 1
        ]?.paymentMethod
          ?.trim()
          ?.toLowerCase();

      await updateOrderStatusApi(
        orderId,
        status,
        paymentMethod,
        adjustType
      );

      setOrder((prev) => {
        if (!prev) return prev;
        const updatedOrders = [...(prev.orders || [])];
        if (updatedOrders[0]) {
          updatedOrders[0] = { ...updatedOrders[0], status };
        }
        return { ...prev, orders: updatedOrders, OrderStatus: status };
      });

      return true;
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
      return false;
    }
  },
  [orderId, order]
);

  /** Local Order Update (for components) */
  const updateOrder = useCallback((updater: (prev: Order | null) => Order | null) => {
    setOrder(updater);
  }, []);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  return {
    order,
    isLoading,
    outstandingAmount,
    fetchOrderDetails,
    updateOrderStatus,
    updateOrder,
    setOrder,
  };
};
